/**
 * SignupPage — Name, email, password registration with Google & Microsoft OAuth.
 *
 * Redirects to /onboarding on success (new users need to create an org).
 */
import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

export function SignupPage() {
  const navigate = useNavigate();
  const { signup, loginWithGoogle, loginWithMicrosoft, isLoading, isAuthenticated, error } =
    useAuthStore();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const ok = await signup(name, email, password);
    if (ok) navigate("/onboarding");
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-sm rounded-xl border border-white/10 bg-surface-raised p-8 shadow-card">
        {/* Logo */}
        <h1 className="mb-2 text-center text-2xl font-bold tracking-tight text-primary">
          AJNT
        </h1>
        <p className="mb-6 text-center text-sm text-muted-foreground">
          Create your account
        </p>

        {/* OAuth */}
        <div className="mb-4 flex flex-col gap-2">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => loginWithGoogle()}
          >
            Sign up with Google
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => loginWithMicrosoft()}
          >
            Sign up with Microsoft
          </Button>
        </div>

        {/* Divider */}
        <div className="mb-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-white/10" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-white/10" />
        </div>

        {/* Email/password form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            label="Name"
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />

          {error && (
            <p className="text-sm text-danger" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" loading={isLoading}>
            Create Account
          </Button>
        </form>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
